/**
 * Where an LTI launch lands (TECH-3186).
 *
 * The server redirects here with `#code=…` after it has verified the platform's
 * `id_token` and resolved the Workspace and User. This route spends that code
 * and gets out of the way. There is nothing to decide and nobody to ask, so it
 * renders a spinner and then either `/app` or `/lti/error`.
 *
 * Web build only. The desktop build routes on the URL hash, which is
 * exactly where the handoff code lives, so a launch can only ever arrive at the
 * web origin. That is the one `ELASO_APP_BASE_URL` names, so the server never
 * sends one anywhere else.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useEffect, useRef } from "react";
import { completeLtiLaunch, takeHandoffCode } from "@/lib/lti/launch-flow";
import { claimSocket } from "@/lib/socket-owner";

export const Route = createFileRoute("/lti/launch")({
	component: LtiLaunchComponent,
});

function LtiLaunchComponent() {
	const navigate = useNavigate();
	// The fragment is read and stripped ONCE per mount cycle, in a ref rather than
	// in the effect body. StrictMode runs the effect twice, and the second run
	// would find an address bar this component had already cleaned.
	const codeRef = useRef<string | null>(null);
	if (codeRef.current === null) {
		codeRef.current = takeHandoffCode();
	}

	useEffect(() => {
		// A launch arrives on the `/app` session, so this surface owns the socket.
		// The sign-in below connects it, and it must not be `/operations`.
		claimSocket("app").catch((error) => {
			console.error("Failed to claim the socket for /app:", error);
		});
	}, []);

	useEffect(() => {
		const code = codeRef.current;
		if (!code) {
			// No code in the fragment. Somebody opened this route directly, or a
			// bookmark of one already spent. Same page they would have got from a
			// second attempt.
			navigate({ to: "/lti/error", search: { code: "code_invalid" } });
			return;
		}

		let active = true;
		completeLtiLaunch(code).then((result) => {
			if (!active) return;
			if (result.ok) {
				navigate({ to: "/app" });
			} else {
				navigate({ to: "/lti/error", search: { code: result.code } });
			}
		});

		return () => {
			active = false;
		};
	}, [navigate]);

	return (
		<div className="flex min-h-screen items-center justify-center p-4">
			<div className="flex flex-col items-center gap-4">
				<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				<p className="text-muted-foreground">Signing you in…</p>
			</div>
		</div>
	);
}
